import React, { forwardRef, type ReactNode } from 'react'
import { Pressable } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { Typography } from '../../typography/Typography'
import { variantForStyleId } from '../../typography/variants'
import { chipSeverityContract } from './chip-severity.contract'

export const CHIP_SEVERITY_LABEL = variantForStyleId(chipSeverityContract.textStyleIds[0])

const TONES = chipSeverityContract.props.tone.values

export type ChipSeverityTone = (typeof TONES)[number]

export type ChipSeverityProps = Omit<
    React.ComponentProps<typeof Pressable>,
    'style' | 'children'
> & {
    children: ReactNode
    /**
     * The severity the chip stands for. Figma's own vocabulary, not the app's:
     * `maintenance` through `crisis` plus `neutral`, and only those six exist.
     */
    tone?: ChipSeverityTone
    selected?: boolean
    /** The glyph takes its ink from the tone — see `variant.icon`. */
    icon?: ReactNode
    className?: string
}

type ChipRef = React.ComponentRef<typeof Pressable>

const variantFor = (selected: boolean, tone: ChipSeverityTone) => {
    const key = `selected=${selected}, tone=${tone}`
    const variant = (
        chipSeverityContract.variants as Record<
            string,
            { container: string; label: string; icon: string; disabled: string }
        >
    )[key]
    if (!variant) {
        throw new Error(`aos-ds ChipSeverity: the Figma contract has no "${key}"`)
    }
    return variant
}

/**
 * A severity chip: the tone colours the glyph, and selecting it thickens the
 * border to the matching status colour rather than filling the chip.
 *
 * Figma draws 30 variants — six tones times `selected` times three states — and
 * the contract folds `state` away: hover becomes a `hover:` prefix and disabled
 * a class applied in JS, because `disabled:` never matches a react-native-web
 * div. Thirty cells become twelve.
 *
 * `hover` exists only where `selected=false` in the file, so a selected chip
 * simply has no hover classes to emit. That is the design's answer, not a gap.
 */
export const ChipSeverity = forwardRef<ChipRef, ChipSeverityProps>(
    (
        {
            children,
            tone = 'neutral',
            selected = false,
            icon,
            disabled = false,
            className,
            ...rest
        },
        ref,
    ) => {
        const variant = variantFor(selected, tone)
        return (
            <Pressable
                ref={ref}
                disabled={disabled}
                role='button'
                accessibilityState={{ selected, disabled: disabled ?? false }}
                className={twMerge(
                    'flex-row items-center justify-center',
                    variant.container,
                    disabled ? variant.disabled : undefined,
                    className,
                )}
                testID={`chip-severity-${tone}`}
                {...rest}
            >
                {icon}
                <Typography variant={CHIP_SEVERITY_LABEL} className={variant.label}>
                    {children}
                </Typography>
            </Pressable>
        )
    },
)

ChipSeverity.displayName = 'ChipSeverity'

export { TONES as CHIP_SEVERITY_TONES }
